/**
 * Growth Path Page
 *
 * Visualizes employee's career growth path and niveau progression.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useAuth } from '../auth';
import { useEvaluations } from '../evaluations/useEvaluations';
import { useNiveausWithStats } from '../niveaus/useNiveaus';
import { ScoreDisplay } from '../evaluations/wizard/ScoreInput';

export function GrowthPathPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: evaluations, isLoading: isLoadingEvals } = useEvaluations();
  const { data: niveaus, isLoading: isLoadingNiveaus } = useNiveausWithStats();
  const employeeId = user?.employeeId;

  // Get employee's evaluations
  const myEvaluations = useMemo(() => {
    return evaluations?.data
      ?.filter((e) => e.employeeId === employeeId && e.status === 'final')
      .sort((a, b) => new Date(a.periodeEnd).getTime() - new Date(b.periodeEnd).getTime()) || [];
  }, [evaluations?.data, employeeId]);

  // Calculate current niveau - default to 1 since we don't have niveau info on TaskAssignment
  // In a real implementation, this would come from the employee's profile
  const currentNiveau = 1;

  // Get scores over time for the chart
  const scoreHistory = useMemo(() => {
    return myEvaluations.map((e) => ({
      date: new Date(e.periodeEnd),
      score: parseFloat(e.totalScore || '0'),
      taskScore: parseFloat(e.taskScore || '0'),
      competencyScore: parseFloat(e.competencyScore || '0'),
    }));
  }, [myEvaluations]);

  // Calculate average score and growth
  const avgScore = scoreHistory.length > 0
    ? scoreHistory.reduce((sum, s) => sum + s.score, 0) / scoreHistory.length
    : 0;
  const lastHistoryScore = scoreHistory[scoreHistory.length - 1]?.score ?? 0;
  const firstHistoryScore = scoreHistory[0]?.score ?? 0;
  const growth = scoreHistory.length >= 2 ? lastHistoryScore - firstHistoryScore : 0;

  // Determine next niveau requirements
  const nextNiveau = currentNiveau < 6 ? currentNiveau + 1 : null;
  const niveausList = niveaus || [];
  const nextNiveauInfo = nextNiveau ? niveausList.find((n) => n.id === nextNiveau) : null;

  if (isLoadingEvals || isLoadingNiveaus) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Groeipad laden...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="growth-path-page" className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Terug naar dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Mijn Groeipad</h1>
          <p className="mt-1 text-sm text-gray-500">
            Volg uw ontwikkeling en ontdek wat nodig is voor het volgende niveau.
          </p>
        </div>

        {/* Current Position */}
        <div className="mb-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Huidig Niveau</p>
              <p className="text-3xl font-bold">Niveau {currentNiveau}</p>
              {niveausList[currentNiveau - 1] ? (
                <p className="text-blue-200 mt-1">{niveausList[currentNiveau - 1]?.name}</p>
              ) : null}
            </div>
            <div className="text-right">
              <p className="text-blue-100 text-sm">Gemiddelde Score</p>
              <p className="text-4xl font-bold">{avgScore > 0 ? avgScore.toFixed(2) : '—'}</p>
              {growth !== 0 && (
                <p className={`text-sm ${growth > 0 ? 'text-green-300' : 'text-red-300'}`}>
                  {growth > 0 ? '▲' : '▼'} {Math.abs(growth).toFixed(2)} groei
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Niveau Progress Visualization */}
        <div className="mb-8 bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Niveau Voortgang</h3>
          <div className="relative">
            {/* Progress Bar */}
            <div className="flex items-center justify-between mb-2">
              {[1, 2, 3, 4, 5, 6].map((niveau) => (
                <div key={niveau} className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                      niveau < currentNiveau
                        ? 'bg-green-500 text-white'
                        : niveau === currentNiveau
                        ? 'bg-blue-600 text-white ring-4 ring-blue-200'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {niveau < currentNiveau ? (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      niveau
                    )}
                  </div>
                  <span className={`text-xs mt-2 ${
                    niveau === currentNiveau ? 'text-blue-600 font-medium' : 'text-gray-500'
                  }`}>
                    {niveau === currentNiveau ? 'Huidig' : `Niveau ${niveau}`}
                  </span>
                </div>
              ))}
            </div>
            {/* Connecting Line */}
            <div className="absolute top-6 left-6 right-6 h-0.5 bg-gray-200 -z-10">
              <div
                className="h-full bg-green-500"
                style={{ width: `${((currentNiveau - 1) / 5) * 100}%` }}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Score History Chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Score Ontwikkeling</h3>
            {scoreHistory.length > 0 ? (
              <div className="flex items-end justify-around gap-2 h-48">
                {scoreHistory.slice(-8).map((entry, index) => {
                  const height = Math.max(10, (entry.score / 5) * 100);
                  const isLatest = index === scoreHistory.slice(-8).length - 1;

                  return (
                    <div key={index} className="flex flex-col items-center">
                      <span className={`text-xs mb-1 ${isLatest ? 'font-bold text-green-600' : 'text-gray-500'}`}>
                        {entry.score.toFixed(1)}
                      </span>
                      <div
                        className={`w-10 rounded-t transition-all ${
                          isLatest ? 'bg-green-500' : 'bg-blue-300'
                        }`}
                        style={{ height: `${height}%` }}
                      />
                      <span className="text-xs text-gray-400 mt-2">
                        {entry.date.toLocaleDateString('nl-NL', { month: 'short', year: '2-digit' })}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-400">
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="mt-2 text-sm">Nog geen beoordelingen</p>
                </div>
              </div>
            )}
          </div>

          {/* Score Breakdown */}
          {scoreHistory.length > 0 && scoreHistory[scoreHistory.length - 1] && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Laatste Beoordeling</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Takenscore (70)</span>
                    <span className="font-medium">{scoreHistory[scoreHistory.length - 1]!.taskScore.toFixed(2)}</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${(scoreHistory[scoreHistory.length - 1]!.taskScore / 5) * 100}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Competentiescore (30)</span>
                    <span className="font-medium">{scoreHistory[scoreHistory.length - 1]!.competencyScore.toFixed(2)}</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-500 rounded-full"
                      style={{ width: `${(scoreHistory[scoreHistory.length - 1]!.competencyScore / 5) * 100}%` }}
                    />
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-900 font-medium">Totaalscore</span>
                    <ScoreDisplay
                      score={Math.round(scoreHistory[scoreHistory.length - 1]!.score)}
                      size="lg"
                      showLabel={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Next Level Requirements */}
        {nextNiveauInfo && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Volgend Niveau: {nextNiveauInfo.name}
                </h3>
                {nextNiveauInfo.beschrijving && (
                  <p className="text-sm text-gray-600 mb-4">{nextNiveauInfo.beschrijving}</p>
                )}
                <div className="bg-white rounded-lg p-4 border border-purple-100">
                  <h4 className="text-sm font-medium text-purple-800 mb-2">Wat is nodig voor dit niveau:</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500">•</span>
                      Consistente prestaties op score 4 of hoger
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500">•</span>
                      Aantoonbare groei in competenties
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500">•</span>
                      Goedkeuring van manager en HR
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tips Section */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="font-medium text-gray-900">Focus op taken</h4>
            </div>
            <p className="text-sm text-gray-500">
              Taken tellen voor 70 mee. Zorg dat u uw toegewezen taken goed uitvoert.
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h4 className="font-medium text-gray-900">Werk aan competenties</h4>
            </div>
            <p className="text-sm text-gray-500">
              Competenties tellen voor 30 mee. Ontwikkel uzelf door training en ervaring.
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h4 className="font-medium text-gray-900">Bespreek met manager</h4>
            </div>
            <p className="text-sm text-gray-500">
              Vraag om feedback en bespreek uw ontwikkeldoelen regelmatig.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
